/**
 * Commercial Head — single living proposal after customer approval.
 *
 * Phase 1 (Draft / Prospect): proposal revisions (R1, R2…) are allowed.
 * Phase 2 (Approved / Won / Projected / Completed): the approved proposal is the
 * Commercial Head. Revisions are forbidden; only Variation Orders may amend scope.
 *
 * Lock is family-wide: if any active (non-cancelled, non-archived) member is locked,
 * no normal -Rn revision may be created anywhere in that family.
 */

import { isProposalApprovedOrWon } from "./proposalReviseWorkspace";
import { canCreateVariationOrder } from "./variationOrderTypes";

/** True when this proposal row is locked as the Commercial Head (no further -Rn revises). */
export function isCommercialHeadLocked(header: {
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}): boolean {
  if (header.cancelledAt) return false;
  return isProposalApprovedOrWon(header);
}

/** Pre-approval revise (-Rn) is allowed only when the head is not locked. */
export function canCreateProposalRevision(header: {
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}): boolean {
  if (header.cancelledAt) return false;
  return !isCommercialHeadLocked(header);
}

/** Post-approval amendments use Variation Orders (D-Tools Phase 1 gate). */
export function shouldUseVariationOrderWorkflow(header: {
  proposalSource?: string | null;
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}): boolean {
  return canCreateVariationOrder(header);
}

export const COMMERCIAL_HEAD_REVISE_BLOCKED_MESSAGE =
  "This proposal has already been approved. Further commercial changes must be made through a Variation Order.";

/** Member shape for family-wide Commercial Head authority (pure). */
export type CommercialHeadFamilyMember = {
  id: number;
  proposalRef: string;
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
  archivedAt?: string | Date | null;
};

function isActiveFamilyMember(m: CommercialHeadFamilyMember): boolean {
  if (m.cancelledAt) return false;
  if (m.archivedAt) return false;
  return true;
}

/**
 * Among active family members, find the authoritative locked Commercial Head.
 * Prefers approvedAt set, then higher id (newer lock stamp).
 */
export function findActiveCommercialHeadInFamily(
  members: ReadonlyArray<CommercialHeadFamilyMember>,
): CommercialHeadFamilyMember | null {
  const locked = members.filter((m) => isActiveFamilyMember(m) && isCommercialHeadLocked(m));
  if (locked.length === 0) return null;
  return [...locked].sort((a, b) => {
    const aApproved = a.approvedAt ? 1 : 0;
    const bApproved = b.approvedAt ? 1 : 0;
    if (bApproved !== aApproved) return bApproved - aApproved;
    return b.id - a.id;
  })[0]!;
}

/** True when no active family member is Commercial Head locked. */
export function familyAllowsNormalProposalRevision(
  members: ReadonlyArray<CommercialHeadFamilyMember>,
): boolean {
  return findActiveCommercialHeadInFamily(members) == null;
}
